import React, { useEffect } from 'react'
import { Route, Switch, Redirect, withRouter } from 'react-router-dom'
import PropTypes from 'prop-types'
import requireAuth from '../helpers/requireAuth'

import SignUpPage from '../pages/SignUpPage/SignUpPage'
import LoginPage from '../pages/LoginPage/LoginPage'
import LoginLinkPage from '../pages/LoginLinkPage/LoginLinkPage'
import ForgotPasswordPage from '../pages/ForgotPasswordPage/ForgotPasswordPage'
import ResetPasswordPage from '../pages/ResetPasswordPage/ResetPasswordPage'
import ActivatePage from '../pages/ActivatePage/ActivatePage'
import TermsAndConditionsPage from '../pages/TermsAndConditionsPage/TermsAndConditionsPage'
import PrivacyPolicyPage from '../pages/PrivacyPolicyPage/PrivacyPolicyPage'
import SupportedBrowsersPage from '../pages/SupportedBrowsersPage/SupportedBrowsersPage'
import Logout from '../components/login/Logout'
import AdminPages from './AdminPages'

import DashboardPage from '../pages/DashboardPage/DashboardPage'
import FleetPage from '../pages/FleetPage/FleetPage'
import FleetAssetPage from '../pages/FleetAssetPage/FleetAssetPage'
import RequestsPage from '../pages/RequestsPage/RequestsPage'
import ViewPlannedMaintenancePage from '../pages/ViewPlannedMaintenancePage/ViewPlannedMaintenancePage'
import ViewServiceRequestPage from '../pages/ViewServiceRequestPage/ViewServiceRequestPage'
import ViewServiceEstimatePage from '../pages/ViewServiceEstimatePage/ViewServiceEstimatePage'
import ViewSalesRequestPage from '../pages/ViewSalesRequestPage/ViewSalesRequestPage'
import ViewPartsRequestPage from '../pages/ViewPartsRequestPage/ViewPartsRequestPage'
import ViewRentalRequestPage from '../pages/ViewRentalRequestPage/ViewRentalRequestPage'
import CreateAssetPage from '../pages/CreateAssetPage/CreateAssetPage'
import SelectAssetPage from '../pages/SelectAssetPage/SelectAssetPage'
import InviteUserPage from '../pages/InviteUserPage/InviteUserPage'
import UsersPage from '../pages/UsersPage/UsersPage'
import ProfilePage from '../pages/ProfilePage/ProfilePage'
import ProfilePasswordPage from '../pages/ProfilePasswordPage/ProfilePasswordPage'

import PartRequestPage from '../pages/PartRequestPage/PartRequestPage'
import RentalRequestPage from '../pages/RentalRequestPage/RentalRequestPage'
import NewEquipmentRequestPage from '../pages/NewEquipmentRequestPage/NewEquipmentRequestPage'
import UsedEquipmentRequestPage from '../pages/UsedEquipmentRequestPage/UsedEquipmentRequestPage'
import UsedEquipmentPage from '../pages/UsedEquipmentPage/UsedEquipmentPage'

import MessageModal from '../components/MessageModal/MessageModal'
import NoMatch from '../components/views/NoMatch'
import StyleGuide from '../components/views/StyleGuide'
import IconReference from '../components/views/IconReference'
import Maintenance from '../components/views/Maintenance'
import useMisc from '../hooks/useMisc'
import useAuth from '../hooks/useAuth'
import AppRoutes from './appRoutes'
import MainComponents from '../components/MainComponents'
import UserLayout from '../components/UserLayout/UserLayout'
import PlannedMaintenancePage from '../pages/PlannedMaintenancePage/PlannedMaintenancePage'
import EquipmentFinderPage from '../pages/EquipmentFinderPage/EquipmentFinderPage'
import LoginLayout from '../components/LoginLayout/LoginLayout'
import CreateRequestStepper from '../pages/CreateRequestStepper.js/CreateRequestStepper'
import CreateRepairRequestFlow from '../pages/CreateRequestStepper.js/components/Repair/CreateRepairRequestFlow'
import CreatePartsRequestFlow from '../pages/CreateRequestStepper.js/components/Parts/CreatePartsRequestFlow'
import UsedEquipmentRequestFormPage from '../pages/UsedEquipmentRequestFormPage/UsedEquipmentRequestFormPage'
import SupportPage from '../pages/SupportPage/SupportPage'
import useOldUser from '../hooks/useOldUser'
import useIntercom from '../hooks/useIntercom'
import { ConnectionBanner } from '../components/ConnectionBanner/ConnectionBanner'
import { useNetworkStatus } from '../utils/networkStatus'
import PendingRequestProcessor from '../utils/processPendingRequests'
import { useAuthActions } from '../store/slices/AuthSlice'

const Routes = props => {
    const {
        location: { pathname },
    } = props
    const {
        handleLogout,
        hasExpired,
        role,
        authenticated: isLoggedIn,
    } = useAuth()
    const { loadIntercom } = useIntercom()
    const { isOffline } = useNetworkStatus()
    const { handleOfflineSessionCheck } = useAuthActions()

    if (window.Intercom != null) {
        window.Intercom('update')
    }
    const {
        checkMaintenanceMode,
        isInMaintenanceMode,
        setIsInMaintenanceMode,
        maintenanceModeMessage,
        maintenanceModeLink,
        getMaintenanceModeLink,
    } = useMisc()

    useEffect(() => {
        checkMaintenanceMode()
    }, [checkMaintenanceMode])

    useEffect(() => {
        if (!isOffline) {
            loadIntercom()
        } else {
            if (window.Intercom) {
                window.Intercom('shutdown')
            }
        }
    }, [isOffline])

    const isLoginRoute = window.location.pathname.includes('/login')
    useEffect(() => {
        const interval = setInterval(() => {
            if (isOffline && !isLoginRoute) {
                handleOfflineSessionCheck()
            } else if (!isOffline) {
                localStorage.setItem('lastLogin', Date.now().toString())
            }
        }, 600000) // Run every 10 minutes

        return () => clearInterval(interval)
    }, [isOffline])

    const isUser = isLoggedIn && role === 'user'
    const isManager = isLoggedIn && role === 'manager'
    const isStandardUser = isUser || isManager

    const resetPasswordLink = '/reset-password/'

    const pagesWithoutNav = [
        '/login',
        '/forgot-password',
        '/signup',
        '/login-link',
        '/mainComponents',
        resetPasswordLink,
        AppRoutes.styleguide(),
    ]
    const hasSideBar =
        !isInMaintenanceMode &&
        isStandardUser &&
        !pagesWithoutNav.includes(pathname)
            ? true
            : false

    const isLoginPath =
        AppRoutes.login().includes(pathname) ||
        AppRoutes.activate().includes(pathname) ||
        AppRoutes.forgotPassword().includes(pathname) ||
        AppRoutes.signUp().includes(pathname) ||
        AppRoutes.loginLink().includes(pathname) ||
        pathname?.includes(resetPasswordLink)

    const { chackOldUserInformation } = useOldUser()

    useEffect(() => {
        if (!isLoginPath) {
            chackOldUserInformation()
        }
    }, [pathname])

    const hasTruckImage =
        AppRoutes.login().includes(pathname) ||
        AppRoutes.forgotPassword().includes(pathname) ||
        AppRoutes.loginLink().includes(pathname) ||
        pathname?.includes(resetPasswordLink)

    const hasScrollPage =
        AppRoutes.signUp().includes(pathname) ||
        AppRoutes.activate().includes(pathname)

    const isAdmin = isLoggedIn && role === 'admin'
    const isSalesperson = isLoggedIn && role === 'salesperson'
    const isElevatedUser = isAdmin || isSalesperson
    return (
        <>
            {/* {Tour} */}
            <Switch>
                <Route exact path={AppRoutes.logout()} component={Logout} />
                <UserLayout hasSideBar={hasSideBar}>
                    <PendingRequestProcessor />
                    <LoginLayout
                        hasScroll={hasScrollPage}
                        isLoginPath={isLoginPath}
                        hasTruckImage={hasTruckImage}
                    >
                        {/* Section 1: Guest pages that should always show. */}
                        <Route
                            exact
                            path={[AppRoutes.home(), AppRoutes.login()]}
                        >
                            {isElevatedUser ? (
                                <Redirect to={AppRoutes.adminRoutes()} />
                            ) : isStandardUser ? (
                                <Redirect to={AppRoutes.dashboard()} />
                            ) : (
                                <Route path="*" component={LoginPage} />
                            )}
                        </Route>
                        <Route path={AppRoutes.activate()}>
                            {isElevatedUser ? (
                                <Redirect to={AppRoutes.adminRoutes()} />
                            ) : isStandardUser ? (
                                <Redirect to={AppRoutes.dashboard()} />
                            ) : (
                                <Route
                                    path={AppRoutes.activate()}
                                    render={props => {
                                        checkMaintenanceMode()
                                        return <ActivatePage {...props} />
                                    }}
                                />
                            )}
                        </Route>
                        <Route exact path={AppRoutes.forgotPassword()}>
                            {isStandardUser ? (
                                <Redirect to={AppRoutes.profilePassword()} />
                            ) : (
                                <Route
                                    path="*"
                                    render={props => {
                                        checkMaintenanceMode()
                                        return <ForgotPasswordPage {...props} />
                                    }}
                                />
                            )}
                        </Route>
                        <Route exact path={AppRoutes.signUp()}>
                            {isElevatedUser ? (
                                <Redirect to={AppRoutes.adminRoutes()} />
                            ) : isStandardUser ? (
                                <Redirect to={AppRoutes.users()} />
                            ) : (
                                <Route
                                    path="*"
                                    render={props => {
                                        checkMaintenanceMode()
                                        return <SignUpPage {...props} />
                                    }}
                                />
                            )}
                        </Route>
                        <Route exact path={AppRoutes.loginLink()}>
                            {isElevatedUser ? (
                                <Redirect to={AppRoutes.adminRoutes()} />
                            ) : isStandardUser ? (
                                <Redirect to={AppRoutes.dashboard()} />
                            ) : (
                                <Route path="*" component={LoginLinkPage} />
                            )}
                        </Route>
                        {/* Guest pages that need the maintenance check. */}
                        <Route path={AppRoutes.resetPassword()}>
                            {isElevatedUser ? (
                                <Redirect to={AppRoutes.adminRoutes()} />
                            ) : isStandardUser ? (
                                <Redirect to={AppRoutes.dashboard()} />
                            ) : (
                                <Route
                                    path={AppRoutes.resetPassword()}
                                    render={props => {
                                        checkMaintenanceMode()
                                        return <ResetPasswordPage {...props} />
                                    }}
                                />
                            )}
                        </Route>
                    </LoginLayout>

                    {/* Page to handle both login-links and requesting them */}

                    <Route
                        exact
                        path={AppRoutes.termsAndConditions()}
                        component={TermsAndConditionsPage}
                    />

                    <Route
                        exact
                        path={AppRoutes.privacyPolicy()}
                        component={PrivacyPolicyPage}
                    />

                    <Route
                        exact
                        path={AppRoutes.maintenance()}
                        render={props => (
                            <Maintenance
                                {...props}
                                setIsInMaintenanceMode={setIsInMaintenanceMode}
                                onCheckMaintenanceMode={checkMaintenanceMode}
                                maintenanceModeMessage={maintenanceModeMessage}
                                maintenanceModeLink={maintenanceModeLink}
                                getMaintenanceModeLink={getMaintenanceModeLink}
                            />
                        )}
                    />

                    {/* Section 2: Nothing below this check will be reachable during Maintenance Mode unless you're an Admin. */}
                    {isInMaintenanceMode && role !== 'admin' ? (
                        <Redirect push from="*" to={AppRoutes.maintenance()} />
                    ) : null}

                    {/* Admin pages that require auth and maintenance mode check only for Salespeople. */}
                    <Route
                        path={AppRoutes.adminRoutes()}
                        render={props => (
                            <AdminPages
                                {...props}
                                checkMaintenanceMode={checkMaintenanceMode}
                            />
                        )}
                    />

                    {/* Section 3: Nothing below this check will be reachable during Maintenance Mode. */}
                    {isInMaintenanceMode ? (
                        <Redirect push from="*" to={AppRoutes.maintenance()} />
                    ) : null}

                    {hasExpired && (
                        <MessageModal
                            isOpen={true}
                            title="Expired Session"
                            message="Your session expired, please sign in again to continue using LiftOne Now"
                            onDone={() => handleLogout()}
                            textDone="Sign In"
                        />
                    )}
                    <ConnectionBanner route={pathname} />

                    {/* User pages to confirm auth and check maintenance mode. */}
                    <Route
                        path={AppRoutes.dashboard()}
                        component={requireAuth(
                            DashboardPage,
                            ['manager', 'user'],
                            checkMaintenanceMode,
                        )}
                    />
                    <Route
                        exact
                        path={AppRoutes.fleet()}
                        component={requireAuth(
                            FleetPage,
                            ['manager', 'user'],
                            checkMaintenanceMode,
                        )}
                    />
                    {/* <Route
                        path={AppRoutes.fleetAssetEdit()}
                        component={requireAuth(
                            FleetAssetEditPage,
                            ['manager', 'user'],
                            checkMaintenanceMode,
                        )}
                    /> */}
                    <Route
                        path={AppRoutes.fleetAsset()}
                        component={requireAuth(
                            FleetAssetPage,
                            ['manager', 'user'],
                            checkMaintenanceMode,
                        )}
                    />
                    <Route
                        path={AppRoutes.supportPage()}
                        component={requireAuth(
                            SupportPage,
                            ['manager', 'user'],
                            checkMaintenanceMode,
                        )}
                    />
                    <Route
                        exact
                        path={AppRoutes.inviteUser()}
                        component={requireAuth(
                            InviteUserPage,
                            ['manager', 'user'],
                            checkMaintenanceMode,
                        )}
                    />
                    <Route
                        exact
                        path={AppRoutes.users()}
                        component={requireAuth(
                            UsersPage,
                            ['manager', 'user'],
                            checkMaintenanceMode,
                        )}
                    />
                    <Route
                        exact
                        path={AppRoutes.profile()}
                        component={requireAuth(
                            ProfilePage,
                            ['manager', 'user'],
                            checkMaintenanceMode,
                        )}
                    />
                    <Route
                        exact
                        path={AppRoutes.profilePassword()}
                        component={requireAuth(
                            ProfilePasswordPage,
                            ['manager', 'user'],
                            checkMaintenanceMode,
                        )}
                    />

                    <Route
                        exact
                        path={AppRoutes.requests()}
                        component={requireAuth(
                            RequestsPage,
                            ['manager', 'user'],
                            checkMaintenanceMode,
                        )}
                    />
                    {/* Redirect to the correct route after the change */}
                    <Route exact path={AppRoutes.requestsExtra()}>
                        <Redirect to={AppRoutes.requests()} />
                    </Route>
                    <Route
                        exact
                        path={AppRoutes.plannedMaintenance()}
                        component={requireAuth(
                            PlannedMaintenancePage,
                            ['manager', 'user'],
                            checkMaintenanceMode,
                        )}
                    />
                    <Route
                        exact
                        path={AppRoutes.equipmentFinder()}
                        component={requireAuth(
                            EquipmentFinderPage,
                            ['manager', 'user'],
                            checkMaintenanceMode,
                        )}
                    />
                    <Route
                        exact
                        path={AppRoutes.createAsset()}
                        component={requireAuth(
                            CreateAssetPage,
                            ['manager', 'user'],
                            checkMaintenanceMode,
                        )}
                    />

                    <Route
                        exact
                        path={AppRoutes.createRequestStepper()}
                        component={requireAuth(
                            CreateRequestStepper,
                            ['manager', 'user'],
                            checkMaintenanceMode,
                        )}
                    />

                    <Route
                        exact
                        path={AppRoutes.createRepairRequestFlow()}
                        component={requireAuth(
                            CreateRepairRequestFlow,
                            ['manager', 'user'],
                            checkMaintenanceMode,
                        )}
                    />

                    <Route
                        exact
                        path={AppRoutes.createPartsRequestFlow()}
                        component={requireAuth(
                            CreatePartsRequestFlow,
                            ['manager', 'user'],
                            checkMaintenanceMode,
                        )}
                    />

                    <Route
                        exact
                        path={AppRoutes.selectAsset()}
                        component={requireAuth(
                            SelectAssetPage,
                            ['manager', 'user'],
                            checkMaintenanceMode,
                        )}
                    />
                    <Route
                        exact
                        path={AppRoutes.partRequests()}
                        component={requireAuth(
                            PartRequestPage,
                            ['manager', 'user'],
                            checkMaintenanceMode,
                        )}
                    />
                    <Route
                        exact
                        path={AppRoutes.rentalRequests()}
                        component={requireAuth(
                            RentalRequestPage,
                            ['manager', 'user'],
                            checkMaintenanceMode,
                        )}
                    />
                    <Route
                        exact
                        path={AppRoutes.newEquipmentRequest()}
                        component={requireAuth(
                            NewEquipmentRequestPage,
                            ['manager', 'user'],
                            checkMaintenanceMode,
                        )}
                    />
                    <Route
                        exact
                        path={AppRoutes.usedEquipmentRequestForm()}
                        component={requireAuth(
                            UsedEquipmentRequestFormPage,
                            ['manager', 'user'],
                            checkMaintenanceMode,
                        )}
                    />
                    <Route
                        exact
                        path={AppRoutes.usedEquipmentRequest()}
                        component={requireAuth(
                            UsedEquipmentRequestPage,
                            ['manager', 'user'],
                            checkMaintenanceMode,
                        )}
                    />
                    <Route
                        path={AppRoutes.usedEquipmentPage()}
                        component={requireAuth(
                            UsedEquipmentPage,
                            ['manager', 'user'],
                            checkMaintenanceMode,
                        )}
                    />
                    <Route
                        path={AppRoutes.viewServiceRequest()}
                        component={requireAuth(
                            ViewServiceRequestPage,
                            ['manager', 'user'],
                            checkMaintenanceMode,
                        )}
                    />
                    <Route
                        path={AppRoutes.viewPlannedMaintenance()}
                        component={requireAuth(
                            ViewPlannedMaintenancePage,
                            ['manager', 'user'],
                            checkMaintenanceMode,
                        )}
                    />
                    <Route
                        path={AppRoutes.viewEstimateRequest()}
                        component={requireAuth(
                            ViewServiceEstimatePage,
                            ['manager', 'user'],
                            checkMaintenanceMode,
                        )}
                    />
                    <Route
                        path={AppRoutes.viewSalesRequest()}
                        component={requireAuth(
                            ViewSalesRequestPage,
                            ['manager', 'user'],
                            checkMaintenanceMode,
                        )}
                    />
                    <Route
                        path={AppRoutes.viewPartsRequest()}
                        component={requireAuth(
                            ViewPartsRequestPage,
                            ['manager', 'user'],
                            checkMaintenanceMode,
                        )}
                    />
                    <Route
                        path={AppRoutes.viewRentalRequest()}
                        component={requireAuth(
                            ViewRentalRequestPage,
                            ['manager', 'user'],
                            checkMaintenanceMode,
                        )}
                    />
                    <Route
                        path={AppRoutes.supportedBrowsers()}
                        component={requireAuth(
                            SupportedBrowsersPage,
                            ['manager', 'user'],
                            checkMaintenanceMode,
                        )}
                    />
                    <Route
                        exact
                        path={AppRoutes.noMatch()}
                        component={NoMatch}
                    />
                </UserLayout>
                <Route path="*" component={NoMatch} />
            </Switch>

            <Route path={'/mainComponents'} render={() => <MainComponents />} />
            <Route exact path={AppRoutes.styleguide()} component={StyleGuide} />
            <Route
                exact
                path={AppRoutes.iconsReference()}
                component={IconReference}
            />
        </>
    )
}

Routes.propTypes = {
    location: PropTypes.shape({
        pathname: PropTypes.string.isRequired,
    }),
}

export default withRouter(Routes)
